import axios from 'axios';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { UploadUrls } from 'types/carrierFileService';

/**
 * Sanitizes a filename by replacing spaces with underscores and removing special characters.
 * Keeps only alphanumeric characters, underscores, and dots (for file extensions).
 *
 * @param fileName - The filename to sanitize.
 * @returns The sanitized filename.
 */
export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9_.]/g, ''); // Remove special characters, keep only alphanumeric, underscores, and dots
};

/**
 * Asynchronously builds a list of file contents as ArrayBuffers from the provided files.
 *
 * @param files - A collection of uploaded files in the `AcceptedFiles` format.
 * @param fileNames - A list of file names to process.
 * @returns A Promise that resolves to an array of ArrayBuffers representing the file contents.
 */
const buildListOfFiles = async (files: AcceptedFiles, fileNames: string[]): Promise<ArrayBuffer[]> => {
  const listOfFiles: Promise<ArrayBuffer>[] = [];

  // Check if the file name exists in the files object before processing it
  // Convert the file content to an ArrayBuffer and add it to the list
  fileNames.forEach((fileName) => {
    if (Object.prototype.hasOwnProperty.call(files, fileName)) {
      listOfFiles.push(files[fileName].file.arrayBuffer());
    }
  });

  return Promise.all(listOfFiles);
};

/**
 * Uploads files to a specified bucket using pre-signed URLs.
 *
 * @param files - A collection of uploaded files in the `AcceptedFiles` format.
 * @param urls - An object containing pre-signed upload URLs, keyed by file names.
 * @param onUpload - A callback function invoked after all files have been successfully uploaded.
 * @returns A Promise that resolves when all files are uploaded.
 */
export const uploadFiles = async (files: AcceptedFiles, urls: UploadUrls, onUpload?: () => void): Promise<void> => {
  // Extract the file names from the URLs object
  const fileNames = Object.keys(urls);
  // Convert the specified files into ArrayBuffers
  const convertedFiles = await buildListOfFiles(files, fileNames);

  // Map each file to an axios PUT request using the corresponding pre-signed URL
  const listOfRequestsForUploadingFiles = convertedFiles.map((convertedFile, index) => {
    const url = urls[fileNames[index]]; // Get the upload URL for the file
    if (!url) throw new Error(`Could not find the file url`);
    return axios.put(url, convertedFile, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });
  });

  if (listOfRequestsForUploadingFiles.length === 0) {
    throw new Error('No valid files to upload. The list of URLs might be empty or files missing.');
  }

  await Promise.all(listOfRequestsForUploadingFiles);
  // Call the callback function after successful uploads
  if (onUpload) {
    onUpload();
  }
};
